import { QuoteCommitment, quoteWindow } from "./quoteCommitments";

// datetime-local is deliberately interpreted as UTC, never the browser timezone.
export function utcInput(value: string) {
  return value && Number.isFinite(Date.parse(value))
    ? new Date(value).toISOString().slice(0, 19)
    : "";
}
export function utcValue(value: string) {
  return value ? `${value}${value.length === 16 ? ":00" : ""}Z` : "";
}
export function nextTrial(serverTime: string) {
  const start = new Date(serverTime);
  if (!Number.isFinite(start.getTime()))
    throw new Error("Refresh server time first.");
  const days = (8 - start.getUTCDay()) % 7 || 7;
  start.setUTCDate(start.getUTCDate() + days);
  start.setUTCHours(0, 0, 0, 0);
  return {
    start: start.toISOString(),
    end: new Date(start.getTime() + 14 * 86400000).toISOString(),
  };
}
export function quoteSummary(rows: QuoteCommitment[]) {
  return rows
    .map(
      (r) =>
        `${r.market}: $${r.minimum_side_notional_usd} on each side${
          r.market.endsWith("-OPTIONS")
            ? ` per contract; ${r.minimum_option_strikes_per_expiry} call-and-put strikes per expiry`
            : ""
        }`
    )
    .join("; ");
}
export function hoursSummary(rows: QuoteCommitment[]) {
  return rows
    .map((r) => {
      const full =
        r.utc_windows.length === 7 &&
        new Set(r.utc_windows.map((w) => w.weekday)).size === 7 &&
        r.utc_windows.every(
          (w) => w.start_minute === 0 && w.end_minute === 1440
        );
      return `${r.market}: ${
        full ? "24/7 UTC" : r.utc_windows.map(quoteWindow).join(", ")
      }`;
    })
    .join("; ");
}
export function validateQuoteRows(rows: QuoteCommitment[]) {
  if (!rows.length)
    throw new Error("Add at least one market and its quoting requirements.");
  const seen = new Set<string>();
  return rows.map((row, index) => {
    const name = row.market.trim();
    const prefix = name || `Market ${index + 1}`;
    if (!name || seen.has(name))
      throw new Error(
        "Choose a different market for each quoting requirement."
      );
    seen.add(name);
    const raw = row.minimum_side_notional_usd.trim();
    if (!/^\d+(\.\d+)?$/.test(raw) || !/[1-9]/.test(raw))
      throw new Error(
        `${prefix}: enter a minimum greater than $0, without commas or a dollar sign.`
      );
    const amount = raw
      .replace(/^0+(?=\d)/, "")
      .replace(/(\.\d*?)0+$/, "$1")
      .replace(/\.$/, "");
    if (!row.utc_windows.length)
      throw new Error(
        `${prefix}: choose 24/7, weekdays or add a custom UTC window.`
      );
    row.utc_windows.forEach((w, i) => {
      if (
        w.weekday < 0 ||
        w.weekday > 6 ||
        w.start_minute < 0 ||
        w.start_minute >= 1440 ||
        w.end_minute > 1440 ||
        w.end_minute <= w.start_minute
      )
        throw new Error(
          `${prefix}: each window must end after it starts. Split overnight hours across two days.`
        );
      if (
        row.utc_windows
          .slice(0, i)
          .some(
            (p) =>
              p.weekday === w.weekday &&
              w.start_minute < p.end_minute &&
              p.start_minute < w.end_minute
          )
      )
        throw new Error(
          `${prefix}: quoting windows on the same day must not overlap.`
        );
    });
    return { ...row, market: name, minimum_side_notional_usd: amount };
  });
}
